import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Users } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Discretion First',
      description: 'What happens with Velmont stays with Velmont. We never discuss our clients, our methods, or our connections. Ever.'
    },
    {
      icon: Clock,
      title: 'Time as Currency',
      description: 'Your time is the only thing we cannot create more of. Every interaction, every experience is designed to respect that truth.'
    },
    {
      icon: Users,
      title: 'Selective by Nature',
      description: 'We accept introductions, not applications. Our network grows through trust, one relationship at a time.'
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-24 md:pt-32">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                Who We Are
              </p>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8 leading-[1.1]">
                A Private Network.<br />
                <span className="italic text-[#A1A1AA]">Not a Company.</span>
              </h1>
              <p className="text-[#A1A1AA] text-base md:text-lg leading-[1.9] font-manrope font-light mb-6">
                Velmont Society is not a concierge service in the traditional sense. We are a 
                carefully cultivated network of relationships — with venue owners, private 
                chefs, pilots, property managers, and the people who make things happen 
                in São Paulo and Rio de Janeiro.
              </p>
              <p className="text-[#A1A1AA] text-base md:text-lg leading-[1.9] font-manrope font-light">
                When you work with us, you're not hiring a service. You're gaining access 
                to a world that took years to build.
              </p>
              <div className="flex items-center gap-5 mt-10">
                <div className="w-16 h-px bg-gold/40" />
                <p className="text-gold/80 text-sm font-cormorant italic tracking-wide">
                  Built on trust. Sustained by discretion.
                </p>
              </div>
            </div>
            <div className="relative h-[500px] lg:h-[600px]">
              <div className="absolute inset-0 border border-white/5 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80"
                  alt="Luxury Experience"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-gold/20" />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian-paper">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
              Our Philosophy
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-10 leading-[1.2]">
              Access cannot be <span className="italic">purchased</span>.<br />
              It must be earned.
            </h2>
            <p className="text-[#A1A1AA] text-base md:text-lg leading-[1.9] font-manrope font-light mb-10">
              The best tables, the private villas, the experiences that never appear 
              online — these exist in a world governed by relationships, not transactions. 
              We've spent years building trust with the people who control access. 
              When you reach out to Velmont, you're leveraging those relationships.
            </p>
            <blockquote className="border-l-2 border-gold pl-8 text-left max-w-2xl mx-auto">
              <p className="font-cormorant text-xl md:text-2xl text-[#F5F5F0] italic mb-4 leading-relaxed">
                "We don't promise the impossible. We deliver what others 
                don't know exists."
              </p>
              <cite className="text-gold/70 text-sm font-manrope not-italic tracking-wide">
                — The Velmont Standard
              </cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
              What Defines Us
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0]">
              Three Principles. <span className="italic">No Exceptions.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="text-center p-10 border border-white/5 hover:border-gold/20 transition-all duration-500"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 border border-gold/30 mb-8">
                  <value.icon className="text-gold/80" size={26} strokeWidth={1.5} />
                </div>
                <h3 className="font-playfair text-2xl text-[#F5F5F0] mb-5">
                  {value.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm leading-[1.8] font-manrope font-light">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian-paper">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            <div className="relative h-[400px] lg:h-auto overflow-hidden border border-white/5">
              <img
                src="https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=800&q=80"
                alt="Luxury Service"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            </div>
            <div className="flex flex-col justify-center">
              <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                The Velmont Difference
              </p>
              <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-8">
                International Standards.<br />
                <span className="italic text-[#A1A1AA]">Brazilian Insider Access.</span>
              </h2>
              <p className="text-[#A1A1AA] text-base leading-[1.9] font-manrope font-light mb-6">
                Brazil offers experiences that rival anywhere in the world — but accessing 
                them requires more than money. It requires knowing the right people, 
                speaking the unspoken language of local luxury, and understanding 
                how things actually work here.
              </p>
              <p className="text-[#A1A1AA] text-base leading-[1.9] font-manrope font-light mb-8">
                We bridge that gap. When you work with Velmont, you get Miami-level 
                service with true Brazilian insider access. The combination is rare. 
                The results speak for themselves.
              </p>
              <Link
                to="/contact"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 text-gold text-xs uppercase tracking-[0.2em] font-medium hover:text-gold-light transition-colors underline-hover w-fit"
              >
                Start a Conversation
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
            Selective Membership
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-8">
            Not for everyone. <span className="italic">For the right ones.</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto mb-12 font-manrope font-light">
            If you value discretion, quality, and access that can't be purchased 
            elsewhere — we should talk.
          </p>
          <Link
            to="/contact"
            data-testid="about-main-cta"
            className="inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 luxury-btn"
          >
            Enter Velmont
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
